import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ConfirmModal from '@/domains/dashboard-management/components/ConfirmModal/ConfirmModal';
import CardDonationPost, {
  type DonationPost,
} from '@/domains/mobile-web/components/CardDonationPost/CardDonationPost';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import {
  useBookRequestComments,
  useDeleteBookRequest,
  useDeleteBookRequestComment,
  useLikeBookRequest,
  useLikeBookRequestComment,
} from '@/domains/mobile-web/hooks/useBookRequests';
import { useCartCount } from '@/domains/mobile-web/hooks/useCart';
import {
  useDeleteKidsPost,
  useDeleteKidsPostComment,
  useKidsPostComments,
  useLikeKidsPost,
  useLikeKidsPostComment,
} from '@/domains/mobile-web/hooks/useKidsPosts';
import type { BookRequest } from '@/domains/mobile-web/types/bookRequest.types';
import type {
  CommunityComment,
  CommunityPost,
  LikeState,
} from '@/domains/mobile-web/types/community.types';
import type { KidsPost } from '@/domains/mobile-web/types/kidsPost.types';
import { toDonationPost } from '@/domains/mobile-web/utils/bookRequest';
import { toMediaPost } from '@/domains/mobile-web/utils/kidsPost';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';

// The post comes from the board (there is no single-post endpoint)
type RequestDetailsLocationState =
  | { from?: 'donation'; post?: BookRequest }
  | { from: 'media'; post?: KidsPost }
  | null;

/** What the open delete confirmation is about */
type PendingDelete = { type: 'post' } | { type: 'comment'; commentId: string };

/** The board a post belongs to: its hooks, card shape and texts */
interface DetailBoard<P extends CommunityPost> {
  from: 'donation' | 'media';
  title: string;
  /** Where the header's back and a deleted post lead */
  boardPath: string;
  showMedia: boolean;
  deletePostMessage: string;
  toPost: (row: P, comments: CommunityComment[]) => DonationPost;
  useComments: typeof useBookRequestComments;
  useLikePost: typeof useLikeBookRequest;
  useDeletePost: typeof useDeleteBookRequest;
  useLikeComment: typeof useLikeBookRequestComment;
  useDeleteComment: typeof useDeleteBookRequestComment;
}

const DONATION_BOARD: DetailBoard<BookRequest> = {
  from: 'donation',
  title: 'Detail Permintaan',
  boardPath: '/store/donation',
  showMedia: false,
  deletePostMessage: 'Apakah Anda yakin ingin menghapus postingan ini?',
  toPost: toDonationPost,
  useComments: useBookRequestComments,
  useLikePost: useLikeBookRequest,
  useDeletePost: useDeleteBookRequest,
  useLikeComment: useLikeBookRequestComment,
  useDeleteComment: useDeleteBookRequestComment,
};

const MEDIA_BOARD: DetailBoard<KidsPost> = {
  from: 'media',
  title: 'Detail Media',
  boardPath: '/store/media',
  showMedia: true,
  deletePostMessage: 'Apakah Anda yakin ingin menghapus postingan ini beserta fotonya?',
  toPost: toMediaPost,
  useComments: useKidsPostComments,
  useLikePost: useLikeKidsPost,
  useDeletePost: useDeleteKidsPost,
  useLikeComment: useLikeKidsPostComment,
  useDeleteComment: useDeleteKidsPostComment,
};

const withLike = <P extends CommunityPost>(row: P, { liked, like_count }: LikeState): P => ({
  ...row,
  liked_by_me: liked,
  like_count,
});

/**
 * The post with every comment; opened by tapping a card on Donasi or Media.
 * Mounted per board (see the key below), so its hooks never switch board.
 */
const RequestDetailsContent = <P extends CommunityPost>({
  board,
  initial,
}: {
  board: DetailBoard<P>;
  initial: P;
}) => {
  const navigate = useNavigate();
  // Kept here: the board cache may not hold this post (another tab, a later page)
  const [row, setRow] = useState(initial);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const comments = board.useComments(row.id, row.comment_count > 0);
  const likePost = board.useLikePost();
  const deletePost = board.useDeletePost();
  const likeComment = board.useLikeComment();
  const deleteComment = board.useDeleteComment();

  const commentRows = comments.data?.pages.flatMap((page) => page.rows) ?? [];
  const post = board.toPost(
    // Every comment is loaded, so the list is the count
    comments.isSuccess ? { ...row, comment_count: commentRows.length } : row,
    commentRows
  );
  const commentsNote = comments.isError
    ? 'Gagal memuat komentar.'
    : row.comment_count > 0 && comments.isLoading
      ? 'Memuat komentar...'
      : undefined;

  /** Shows the like at once; the server answer (or the old state on failure) replaces it */
  const toggleLike = () => {
    const before = row;
    const liked = !before.liked_by_me;
    setRow(
      withLike(before, {
        liked,
        like_count: Math.max(0, before.like_count + (liked ? 1 : -1)),
      })
    );
    likePost.mutate(
      { postId: before.id, liked },
      {
        onSuccess: (state) => setRow((current) => withLike(current, state)),
        onError: () => setRow(before),
      }
    );
  };

  const closeDelete = () => setPendingDelete(null);
  const confirmDelete = () => {
    if (pendingDelete?.type === 'post') {
      deletePost.mutate(row.id, {
        onSuccess: () => navigate(board.boardPath, { replace: true }),
        onSettled: closeDelete,
      });
    } else if (pendingDelete?.type === 'comment') {
      deleteComment.mutate(
        { postId: row.id, commentId: pendingDelete.commentId },
        { onSettled: closeDelete }
      );
    }
  };

  return (
    <>
      <CardDonationPost
        post={post}
        showMedia={board.showMedia}
        commentsNote={commentsNote}
        onLike={toggleLike}
        onReply={() =>
          navigate(`/store/reply/${row.id}`, {
            state: { from: board.from, post: row, returnBack: true },
          })
        }
        onDelete={row.is_mine ? () => setPendingDelete({ type: 'post' }) : undefined}
        onLikeComment={(comment) =>
          likeComment.mutate({
            postId: row.id,
            commentId: comment.id,
            liked: !comment.isLiked,
          })
        }
        onDeleteComment={(comment) => setPendingDelete({ type: 'comment', commentId: comment.id })}
      />
      <ConfirmModal
        open={pendingDelete !== null}
        title={pendingDelete?.type === 'comment' ? 'Hapus Komentar ?' : 'Hapus Postingan ?'}
        message={
          pendingDelete?.type === 'comment'
            ? 'Apakah Anda yakin ingin menghapus komentar ini?'
            : board.deletePostMessage
        }
        confirmLabel="Ya, Hapus"
        variant="danger"
        loading={deletePost.isPending || deleteComment.isPending}
        onConfirm={confirmDelete}
        onClose={closeDelete}
      />
    </>
  );
};

const RequestDetails = () => {
  const navigate = useNavigate();
  const cartCount = useCartCount();
  const { id } = useParams<{ id: string }>();
  const state = useLocation().state as RequestDetailsLocationState;
  const board = state?.from === 'media' ? MEDIA_BOARD : DONATION_BOARD;

  const renderContent = () => {
    if (!state?.post || state.post.id !== id) {
      return <p className="reg12 text-text-grey text-center py-6">Postingan tidak ditemukan.</p>;
    }
    return state.from === 'media' ? (
      <RequestDetailsContent
        key={`media-${state.post.id}`}
        board={MEDIA_BOARD}
        initial={state.post}
      />
    ) : (
      <RequestDetailsContent
        key={`donation-${state.post.id}`}
        board={DONATION_BOARD}
        initial={state.post}
      />
    );
  };

  return (
    <main className="min-h-screen">
      <HeaderMobile>
        <TopBarHeader
          type="textBucket"
          onBack={() => navigate(board.boardPath)}
          totalProduct={cartCount}
          title={board.title}
          onAction={() => navigate('/store/cart')}
        />
      </HeaderMobile>

      <WrapperMobile className="pt-3">{renderContent()}</WrapperMobile>
    </main>
  );
};

export default RequestDetails;
